import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MapPin, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { parseGmapsUrl, isShortGmapsLink, gmapsUrlFromCoords } from "@/lib/gmaps";

/**
 * Single "Link Google Maps" input that extracts { lat, lng } from a pasted URL and
 * writes them onto the parent form. Preserves an "Buka Peta" preview button and shows
 * the currently extracted coordinates for transparency.
 *
 * Props:
 *   lat, lng      current numeric values (or "")
 *   onCoords      (nextLat, nextLng) => void   — called when a new URL is parsed
 *   testidPrefix  string
 */
export default function GmapsLocationInput({ lat, lng, onCoords, testidPrefix = "gmaps-loc" }) {
  const [raw, setRaw] = useState(lat && lng ? gmapsUrlFromCoords(lat, lng) : "");

  const commit = (val) => {
    setRaw(val);
    if (!val) { onCoords(null, null); return; }
    const parsed = parseGmapsUrl(val);
    if (parsed) {
      onCoords(parsed.lat, parsed.lng);
    } else if (isShortGmapsLink(val)) {
      toast.error("Link pendek (goo.gl / maps.app.goo.gl) belum bisa diurai otomatis. Buka link tersebut di browser, salin URL lengkap dari address bar, lalu tempel di sini.");
    }
    // Silently ignore other malformed inputs; user still typing.
  };

  const previewUrl = gmapsUrlFromCoords(lat, lng);
  const hasCoords = !!(lat && lng);

  return (
    <div data-testid={`${testidPrefix}-wrapper`}>
      <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-1 flex items-center gap-1.5">
        <MapPin size={11} /> Link Google Maps
        <span className="text-[10px] normal-case tracking-normal opacity-70">(auto-ekstrak lat/lng)</span>
      </div>
      <div className="flex gap-2">
        <Input
          placeholder="Tempel URL: https://www.google.com/maps/place/…/@-6.2434,106.8006,17z"
          value={raw}
          onChange={(e) => commit(e.target.value)}
          data-testid={`${testidPrefix}-input`}
          className="flex-1"
        />
        {previewUrl ? (
          <Button
            type="button"
            variant="outline"
            className="rounded-full whitespace-nowrap"
            onClick={() => window.open(previewUrl, "_blank", "noopener,noreferrer")}
            data-testid={`${testidPrefix}-preview`}
          >
            <ExternalLink size={12} className="mr-1" /> Buka Peta
          </Button>
        ) : null}
      </div>
      {hasCoords ? (
        <div className="mt-1 text-[11px] text-[#4B5563]" data-testid={`${testidPrefix}-coords`}>
          Koordinat terdeteksi: <strong className="text-[#1F2937]">{Number(lat).toFixed(6)}, {Number(lng).toFixed(6)}</strong>
        </div>
      ) : (
        <div className="mt-1 text-[11px] text-[#4B5563]">Belum ada koordinat. Buka Google Maps, cari sekolah, klik "Bagikan → Salin tautan", lalu tempel URL-nya di atas.</div>
      )}
    </div>
  );
}
